import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { MessageSquarePlus, Sparkles, X } from "lucide-react";
import type { TextPick } from "./PreviewFrame";
import { LaneNote } from "./LaneNote";
import { Spinner } from "./Spinner";
import { usePopoverPlacement } from "../hooks/usePopoverPlacement";
import { useKeepInView } from "../hooks/useKeepInView";

interface TextEditorProps {
  pick: TextPick;
  /** What this text is, e.g. "Title" or "Button text". */
  label: string;
  /** Where else the same text appears, when known. */
  note: string | null;
  saving: boolean;
  error: boolean;
  onSave: (text: string, onlyHere: boolean) => void;
  onCancel: () => void;
  simple?: boolean;
  onAskRewrite?: () => void;
  onComment?: () => void;
  onlyHereAvailable?: boolean;
  /** Point to change: where these words sit, with the levels above selectable instead. */
  trail?: ReactNode;
}

const MAX_TEXT = 4_000;

/**
 * Edits one piece of text where it sits on the page: a popover beside it on wide screens, a sheet
 * at the bottom on phones. Saving changes the same text everywhere it appears.
 */
export function TextEditor({ pick, label, note, saving, error, onSave, onCancel, simple, onAskRewrite, onComment, onlyHereAvailable = false, trail }: TextEditorProps) {
  const [text, setText] = useState(pick.text);
  const [onlyHere, setOnlyHere] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const editor = useRef<HTMLFormElement>(null);
  const placement = usePopoverPlacement(editor, pick.left, pick);
  const view = useKeepInView(editor, `${pick.top}:${pick.bottom}:${placement.above}`);
  useEffect(() => {
    setText(pick.text);
    setOnlyHere(false);
    field.current?.focus({ preventScroll: true });
    field.current?.select();
  }, [pick]);

  const changed = text.trim() !== pick.text.trim();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (changed && text.trim()) onSave(text.trim(), onlyHere);
  };

  return (
    <form
      ref={editor}
      className="bw-text-editor"
      data-tether={placement.above ? "above" : "below"}
      style={{ top: placement.above ? pick.top - 10 : Math.max(8, pick.bottom + 10), transform: view.transform(placement.above ? "translateY(-100%)" : undefined), ...view.style, left: placement.left, "--bw-tether": `${placement.tether}px` } as CSSProperties}
      onSubmit={submit}
      aria-labelledby="bw-text-editor-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onCancel();
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) submit(event);
      }}
    >
      <div className="bw-text-editor-head">
        {trail ?? <strong id="bw-text-editor-title">{label}</strong>}
        <button type="button" className="bw-btn bw-btn--icon" onClick={onCancel} aria-label="Close without saving">
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <label>
        <span className="bw-sr">{label}</span>
        <textarea ref={field} rows={Math.min(6, Math.max(2, Math.ceil(text.length / 48)))} value={text} maxLength={MAX_TEXT} onChange={(event) => setText(event.target.value)} />
      </label>
      {note && <p className="bw-item-note">{note}</p>}
      {onlyHereAvailable && <label className="bw-text-editor-scope">
        <input type="checkbox" checked={onlyHere} onChange={(event) => setOnlyHere(event.target.checked)} />
        <span>Only change here</span>
      </label>}
      {error && <p className="bw-item-note" role="alert">That didn’t save. Your page is back as it was. Try again.</p>}
      <div className="bw-actions bw-text-editor-actions">
        <button type="button" className="bw-btn bw-btn--quiet" onClick={onCancel}>Cancel</button>
        <button type="submit" className="bw-btn bw-btn--primary" disabled={!changed || !text.trim() || saving}>{saving && <Spinner />}{saving ? "Saving…" : "Save"}</button>
      </div>
      {simple && <div className="bw-text-editor-secondary-actions">
        <button type="button" className="bw-btn bw-btn--text" onClick={onAskRewrite}><Sparkles size={16} aria-hidden="true" />Ask Stax to rewrite</button>
        <button type="button" className="bw-btn bw-btn--text" onClick={onComment}><MessageSquarePlus size={16} aria-hidden="true" />Comment</button>
      </div>}
      <LaneNote lane="instant" />
    </form>
  );
}
